import { type Offer, type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { useWorkerStats } from "@src/appV2/Accounts/WorkplaceReferrals/api/useWorkerStats";
import { useGetWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShifts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMemo } from "react";

import { useFetchSubstage } from "../../ClipboardScore/hooks/useFetchSubstage";
import { useShouldEducateNewWorker } from "../Booking/useShouldEducateNewWorker";
import { useShouldShowClipboardScoreEducation } from "../Booking/useShouldShowClipboardScoreEducation";
import { groupShiftsIntoBundles } from "./groupShiftsIntoBundles";
import type { ShiftBundle } from "./types";
import { useShiftBundlesConfig } from "./useShiftBundlesConfig";

interface UseShiftBundlesOptions {
  eligibleWorkplaceIds: Set<string>;
  offersMap?: Map<string, Offer>;
}

export function useShiftBundles(
  shifts: OpenShift[],
  options: UseShiftBundlesOptions
): Map<string, ShiftBundle> {
  const config = useShiftBundlesConfig();
  const { eligibleWorkplaceIds, offersMap } = options;
  const worker = useDefinedWorker();

  // Fetch worker stats to determine if the worker should see bundles.
  // Workers who would require new education or are limited by basket-size
  // are excluded
  const { data: workerStats } = useWorkerStats({ agentId: worker.userId });
  const workerPastShiftsCount = workerStats?.completedShiftsCount;

  const { shouldEducateNewWorker } = useShouldEducateNewWorker(workerPastShiftsCount);
  const { shouldShowClipboardScoreEducation } =
    useShouldShowClipboardScoreEducation(workerPastShiftsCount);

  const subStage = useFetchSubstage();
  const isWorkerBasketSizeLimited = subStage.data?.name === "LIMITED";

  // Fetch booked shifts
  const {
    shifts: bookedShifts,
    isLoading: isLoadingBookedShifts,
    isErrored: isErroredBookedShifts,
  } = useGetWorkerShifts({ worker });

  // Derive stable primitives from Set/Map so the downstream useMemo doesn't re-run on every render.
  // These recompute every render (their inputs are reference-unstable), but since the resulting
  // strings are compared by value, the expensive groupShiftsIntoBundles call is skipped when contents haven't changed.
  const eligibleWorkplaceIdsKey = [...eligibleWorkplaceIds].sort().join(",");
  const offersMapKey = offersMap ? [...offersMap.keys()].sort().join(",") : "";

  return useMemo(() => {
    if (!config.enabled) {
      return new Map<string, ShiftBundle>();
    }

    if (shouldEducateNewWorker || shouldShowClipboardScoreEducation || isWorkerBasketSizeLimited) {
      return new Map<string, ShiftBundle>();
    }

    if (isLoadingBookedShifts || isErroredBookedShifts) {
      return new Map<string, ShiftBundle>();
    }

    const bookedShiftData = bookedShifts.map((shift) => ({
      start: shift.start,
      end: shift.end,
    }));

    return groupShiftsIntoBundles({
      shifts,
      config,
      eligibleWorkplaceIds,
      bookedShifts: bookedShiftData,
      offersMap,
    });
    // Only eligibleWorkplaceIds and offersMap are removed, whose changes
    // are covered by eligibleWorkplaceIdsKey and offersMapKey respectively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shifts,
    config,
    shouldEducateNewWorker,
    shouldShowClipboardScoreEducation,
    isWorkerBasketSizeLimited,
    eligibleWorkplaceIdsKey,
    bookedShifts,
    isLoadingBookedShifts,
    isErroredBookedShifts,
    offersMapKey,
  ]);
}
